/**
 * Namen der Firestore-Collections des Portals.
 *
 * Sie sind KEINE Erfindung der App: Der Export der Pipeline schreibt genau
 * diese Collections, und die Functions lesen sie unter denselben Namen
 * (`packages/functions/src/shared/collections.ts`). Hier stehen sie, weil die
 * App die Fach-Daten direkt liest — sie sind öffentlich, read-only und liegen
 * in einer eigenen Datenbank; ein Function-Aufruf davor wäre nur Latenz.
 *
 * Perspektivisch gehören sie wie die Call-Shapes ins Model: Dann teilen sich
 * Pipeline, Functions und App eine Wahrheit statt dreier Listen.
 */

/** Drucksachen — ein Aktenzeichen, ein Weg durch die Gremien. */
export const COL_PAPERS = "papers";

/** Stationen: eine Drucksache in einem Gremium, mit Funktion und Ergebnis. */
export const COL_BERATUNGEN = "beratungen";

/** Sitzungen samt Tagesordnung. */
export const COL_MEETINGS = "meetings";

/** Gremien — Rat, Ausschüsse, Bezirksvertretungen, Fraktionen. */
export const COL_ORGANIZATIONS = "organizations";

/**
 * Person ↔ Gremium mit Rolle und Zeitraum (seit 2004). Laufende Zeilen haben
 * kein Enddatum; gefiltert wird im Browser — kein Sortierfeld, kein Index.
 */
export const COL_MITGLIEDSCHAFTEN = "mitgliedschaften";

/** Kuratierte Parteien-Tabelle: Kurzname und Farbe zu einem Slug. */
export const COL_PARTEIEN = "parteien";

/**
 * Suchindex — heute eine Sammlung, eigentlich ein Artefakt.
 *
 * Er ist für alle identisch und ändert sich je Lauf einmal; sein richtiger
 * Platz ist das Hosting, wo ein CDN ihn umsonst ausliefert. Solange er hier
 * liegt, wird er erst geladen, wenn jemand tatsächlich sucht.
 */
export const COL_SUCHINDEX = "suchindex";

// =============================================================================
// Bausteine der Startseite — Verzeichnisse und Aufmacher
// =============================================================================

/**
 * Nachschlagewerke: eine Liste je Dokument.
 *
 * Den Namen gibt es in BEIDEN Datenbanken mit verschiedenem Inhalt — welche
 * gemeint ist, entscheidet der Zugriff (`services.database` gegen
 * `services.databaseNamed(DB_SHARED)`), nicht der Name.
 */
export const COL_VERZEICHNISSE = "verzeichnisse";

/** Was es im Gebiet Neues gibt — ein Dokument je Bezirk, plus "stadtweit". */
export const COL_AUFMACHER = "aufmacher";

/** Die Dokumente der geteilten Datenbank: portalübergreifend gültig. */
export const DOK_GEBIETE = "gebiete";
export const DOK_PARTEIEN = "parteien";
export const DOK_WAHLEN = "wahlen";

/**
 * Bodenrichtwerte — Kennzahlen je Gebiet, dazu die amtlichen Einzelzonen.
 *
 * Dasselbe Muster wie Strukturdaten und ihre Indikatoren-Tabelle: die Werte in
 * einer Collection, die Beschriftung der Codes in einem Verzeichnis-Dokument.
 * Alles in der GETEILTEN Datenbank — was der Boden wert ist, ist keine Frage
 * dieses Portals allein.
 */
export const COL_BODENRICHTWERTE = "bodenrichtwerte";
export const COL_BODENRICHTWERTZONEN = "bodenrichtwertzonen";
export const DOK_BODENRICHTWERTE = "bodenrichtwerte";

/** Die Dokumente der Politik-Datenbank: an dieses Portal gebunden. */
export const DOK_GREMIEN = "gremien";
export const DOK_FRAKTIONEN = "fraktionen";
export const DOK_DATENSTAND = "datenstand";

/** Die Dezernate der Stadtverwaltung — kuratiert aus dem Verteilungsplan. */
export const DOK_DEZERNATE = "dezernate";

/** Woher kommt was — eine Zeile je Datensatz (ANFORDERUNG-quellen-verzeichnis.md). */
export const DOK_QUELLEN = "quellen";

/** Die Gremien, die es in der Stadt GIBT — amtliche Übersicht, kuratiert. */
export const DOK_GREMIEN_DER_STADT = "gremien-der-stadt";

/**
 * Sitzverteilung des Rates — von Hand aus dem amtlichen Sitzplan übertragen.
 * Kein Verzeichnis mit `eintraege`, sondern ein Dokument mit eigenen Feldern.
 */
export const DOK_SITZVERTEILUNG = "sitzverteilung";
/**
 * Die kuratierte Indikatoren-Tabelle — in der GETEILTEN Datenbank.
 *
 * Strukturdaten sind portalübergreifend: Ein Verkehrs- oder Kulturportal
 * derselben Stadt braucht dieselben Indikatoren mit denselben Einheiten.
 */
export const DOK_INDIKATOREN = "indikatoren";
/** Die gerechnete Sitzverteilung der neun Bezirksvertretungen (Datenbank politik). */
export const DOK_BV_SITZVERTEILUNG = "bv-sitzverteilung";

/**
 * Kennzahlen ALLER Gremien in einem Dokument — Rat, Ausschüsse, BVen.
 *
 * Nicht "bv-kennzahlen": Der Zuschnitt ist nicht bezirksvertretungs-spezifisch,
 * nur das Fenster und der Bezugsschlüssel sind es.
 */
export const DOK_GREMIEN_KENNZAHLEN = "gremien-kennzahlen";
/**
 * Die Parameter des BV-Wahlverfahrens — in der GETEILTEN Datenbank.
 *
 * Das Verfahren ist Landesrecht, kein Kölner Inhalt: Ein Portal einer anderen
 * NRW-Stadt braucht dieselben Werte.
 */
export const DOK_WAHLVERFAHREN_BV = "wahlverfahren-bezirksvertretung";
/** Eine Zeile je Gebiet und Jahr, rund 170 Werte — ebenfalls geteilt. */
export const COL_STRUKTURDATEN = "strukturdaten";
/**
 * Die Wahl-Gliederung — GETEILT, und quer zur kommunalen.
 *
 * Stimmbezirke tragen sowohl ihre Wahl-Nummer als auch die amtliche
 * Stadtteilnummer; sie sind damit die Brücke zwischen beiden Welten. Die
 * Wahlräume hängen an der Stimmbezirks-Nummer, nicht am Stadtteil — deshalb
 * geht der Weg immer über die Stimmbezirke.
 */
export const COL_STIMMBEZIRKE = "stimmbezirke";
export const COL_WAHLRAEUME = "wahlraeume";

/**
 * Ein Dokument je Teilwahl und Gebiet, fertig für die Detailseite — GETEILT.
 *
 * ID `{teilwahlId}:{gebietId}`; das vollständige Ergebnis, die Sitze (wo auf
 * dieser Ebene vergeben) und die vorige Wahl derselben Art, im Export
 * zusammengesetzt. Die Detailseite ist damit ein Lesevorgang, kein Index.
 */
export const COL_ERGEBNIS_SICHTEN = "ergebnis-sichten";

/** Ein Dokument je Partei (shared) — Sitze, Wahlergebnisse, Mitgliedschaften denormalisiert (FACHKONZEPT-parteien.md 8.1). */
export const COL_PARTEI_SICHTEN = "partei-sichten";

/**
 * Logischer Name der geteilten Datenbank (`services.databaseNamed(DB_SHARED)`).
 *
 * Verdrahtet in `provider-setup.ts` — hier steht nur der Name, damit
 * Aufrufstellen ihn nicht raten.
 */
export const DB_SHARED = "shared";
